import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";

export default function PatientList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    const load = () => api.get("/patients", { params: { q: q || undefined } }).then(({ data }) => setRows(data)).catch(() => {});
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q]);

  const summary = useMemo(() => ({
    total: rows.length,
    admitted: rows.filter(r => r.status === "admitted").length,
    critical: rows.filter(r => r.critical).length,
  }), [rows]);

  return (
    <div className="space-y-6" data-testid="patient-list">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Patient registry</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Patients</h1>
          <p className="text-sm text-muted-foreground mt-1">{summary.total} results · {summary.admitted} admitted · {summary.critical} critical</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, ID, bed, phone, consultant…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-full md:w-96" data-testid="patient-search" />
        </div>
      </div>

      <div className="card-clinical overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-2">Hospital ID</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Consultant</div>
          <div className="col-span-1">Bed</div>
          <div className="col-span-2">Diagnosis</div>
          <div className="col-span-1">DOL</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {rows.length === 0 && <div className="p-8 text-sm text-muted-foreground text-center">No patients found.</div>}
        {rows.map((p) => (
          <Link key={p.id} to={`/patients/${p.id}`} className="grid grid-cols-12 px-4 py-3 border-b border-border/60 hover:bg-accent/40 items-center text-sm" data-testid={`patient-row-${p.hospital_id}`}>
            <div className="col-span-2 font-mono text-xs">{p.hospital_id}</div>
            <div className="col-span-3 font-medium truncate">
              {p.name}
              {p.critical && <Badge variant="destructive" className="ml-2 rounded-full text-[10px]">CRITICAL</Badge>}
            </div>
            <div className="col-span-2 text-muted-foreground truncate">{p.consultant || "—"}</div>
            <div className="col-span-1 truncate">{p.bed_number || "—"}</div>
            <div className="col-span-2 text-muted-foreground truncate">{p.diagnosis || "—"}</div>
            <div className="col-span-1 tabular">{p.day_of_life ?? "—"}</div>
            <div className="col-span-1 text-right">
              <span className={`text-[10px] rounded-full px-2 py-0.5 ${p.status === "admitted" ? "bg-vacant" : "bg-muted text-muted-foreground"}`}>
                {p.status || "—"}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
